/**
 * Phase 35 Session 35.2a — In-repo MDX fallback runtime.
 *
 * 35.1a's backend resolver loads `docs/help/tooltips/<helpKey>.mdx`
 * files into a process-wide map at boot so the API can serve a
 * tooltip body even when Sanity is unreachable. This module is the
 * client-side counterpart: each consumer app registers its
 * build-bundled fallback set once at boot, and `useTooltip(helpKey)`
 * falls through to it when the network round-trip fails.
 *
 * The actual build plugin that scans `docs/help/tooltips/*.mdx` and
 * compiles them into a typed `fallbacks.generated.ts` module ships
 * in **35.2b** (Vite plugin for dashboard / pos-web / storefront +
 * Metro transformer for RN POS / Management). 35.2a delivers the
 * runtime registry + lookup API so consumers can wire their own
 * imports today (or skip wiring entirely — `resolveTooltipFallback`
 * gracefully returns null when no fallback was registered).
 *
 * Design constraints:
 *   - **Synchronous lookup.** A flaky network must never blank the
 *     UI; calling `useTooltip` returns the fallback in the same tick
 *     React renders the tooltip. No async dance.
 *   - **No gating in the fallback.** Gating happens server-side; the
 *     fallback is the disaster-recovery floor, not the policy
 *     enforcement layer. Consumers of fallbacks already passed the
 *     `help.read` gate (the `?` icon doesn't render at all when they
 *     haven't).
 *   - **Locale-aware.** Each fallback can register multiple locales;
 *     `resolveTooltipFallback(helpKey, 'fr-FR')` falls through to
 *     `en-US` when the requested locale isn't available so a
 *     French-locale dashboard never renders a blank tooltip.
 */

/**
 * Single fallback entry. Authored from `docs/help/tooltips/<helpKey>.mdx`
 * frontmatter. Body is plain Markdown — the SDK's Portable Text
 * renderer handles it transparently.
 */
export interface TooltipFallback {
  helpKey: string;
  body: string;
  /** Optional title; the dashboard reader uses it as the heading
   *  above the body. Tooltip popovers ignore it. */
  title?: string;
  /** BCP-47 locale; defaults to `en-US` when omitted in frontmatter. */
  locale?: string;
  /** Optional article id — surfaces a "Read more →" link in the
   *  tooltip popover. */
  linkToArticleId?: string;
}

interface FallbackRegistry {
  /** Map<helpKey, Map<locale, fallback>> */
  byKey: Map<string, Map<string, TooltipFallback>>;
}

const registry: FallbackRegistry = { byKey: new Map() };

const DEFAULT_LOCALE = 'en-US';

/**
 * Replace the entire registry with a fresh batch. Called once at app
 * boot from the build-plugin-generated module. Idempotent on
 * identical input — re-registering the same set is a no-op (HMR /
 * fast-refresh friendly).
 *
 * Bulk replacement (vs. additive register) means the runtime always
 * matches what the build emitted; a stale entry from a prior boot
 * can't survive a hot-reload.
 */
export function registerTooltipFallbacks(
  fallbacks: readonly TooltipFallback[],
): void {
  const next = new Map<string, Map<string, TooltipFallback>>();
  for (const fallback of fallbacks) {
    if (!fallback || typeof fallback.helpKey !== 'string') continue;
    if (!fallback.helpKey.trim()) continue;
    const locale = normalizeLocale(fallback.locale);
    let perKey = next.get(fallback.helpKey);
    if (!perKey) {
      perKey = new Map();
      next.set(fallback.helpKey, perKey);
    }
    perKey.set(locale, { ...fallback, locale });
  }
  registry.byKey = next;
}

/**
 * Synchronous lookup. Returns null when no fallback exists for the
 * given (helpKey, locale) pair. Caller shouldn't surface anything
 * when null — gating + entitlement already determined whether the
 * tooltip should render at all.
 *
 * Locale resolution:
 *   1. Exact match on the requested locale.
 *   2. Match on the locale's primary subtag (`en-GB` → `en` → falls
 *      to whichever en-* fallback we have — typically `en-US`).
 *   3. `en-US` as the default.
 *   4. Any registered locale, if `en-US` isn't in the set.
 *   5. Null.
 */
export function resolveTooltipFallback(
  helpKey: string,
  locale: string = DEFAULT_LOCALE,
): TooltipFallback | null {
  if (!helpKey || typeof helpKey !== 'string') return null;
  const perKey = registry.byKey.get(helpKey);
  if (!perKey || perKey.size === 0) return null;

  const requested = normalizeLocale(locale);
  const exact = perKey.get(requested);
  if (exact) return exact;

  // Primary subtag fallback: 'fr-CA' → look for any 'fr-*'.
  const primary = requested.split('-')[0];
  if (primary) {
    for (const [registeredLocale, fallback] of perKey.entries()) {
      if (registeredLocale.split('-')[0] === primary) return fallback;
    }
  }

  // English default.
  const enUs = perKey.get(DEFAULT_LOCALE);
  if (enUs) return enUs;

  // Last resort: whatever's there.
  const first = perKey.values().next();
  return first.done ? null : first.value;
}

/** Returns true when at least one fallback exists for the helpKey. */
export function hasTooltipFallback(helpKey: string): boolean {
  if (!helpKey || typeof helpKey !== 'string') return false;
  const perKey = registry.byKey.get(helpKey);
  return perKey !== undefined && perKey.size > 0;
}

/**
 * Read the registered fallback count + helpKey set. Useful for the
 * dashboard's debug surface (Phase 35.3 admin pane shows "X tooltips
 * fallback-bundled in this build") + for spec assertions.
 */
export function listRegisteredFallbacks(): {
  helpKeys: string[];
  totalEntries: number;
} {
  let totalEntries = 0;
  const helpKeys: string[] = [];
  for (const [key, perKey] of registry.byKey.entries()) {
    helpKeys.push(key);
    totalEntries += perKey.size;
  }
  helpKeys.sort();
  return { helpKeys, totalEntries };
}

/**
 * Test/teardown helper. Wipes the registry so a spec can re-register
 * cleanly. Not exported from the package barrel.
 */
export function _resetTooltipFallbacks(): void {
  registry.byKey = new Map();
}

function normalizeLocale(raw: unknown): string {
  if (typeof raw !== 'string') return DEFAULT_LOCALE;
  const trimmed = raw.trim();
  if (!trimmed) return DEFAULT_LOCALE;
  return trimmed;
}
